import { useCallback, useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import {
  fillCharSpans,
  killTweensOf,
  playHoverRipple,
  playParticleBurst,
  playTextHoverStagger,
  runIdleAnimations,
} from './heroMagneticButtonAnimations';

const INNER_SIZE = {
  md: 'min-h-[48px] w-full items-center justify-center gap-2 px-6 py-3.5 text-[0.9375rem] sm:min-h-0 sm:text-[0.95rem]',
  sm: 'min-h-0 w-auto items-center justify-center gap-2 px-5 py-2.5 text-sm',
  submit: 'min-h-[48px] w-auto items-center justify-center gap-2 px-6 py-3 text-sm',
};

const MAGNETIC_WIDTH = {
  md: 'relative z-10 block w-full max-w-full transform-gpu will-change-transform sm:w-auto',
  sm: 'relative z-10 inline-block w-auto max-w-full transform-gpu will-change-transform',
  submit: 'relative z-10 inline-block w-auto max-w-full transform-gpu will-change-transform',
};

/** Full-width `md` chip: no `sm:w-auto` so the control stays as wide as its column. */
const MAGNETIC_WIDTH_MD_FULL = 'relative z-10 block w-full max-w-full transform-gpu will-change-transform';

/**
 * Reusable magnetic CTA (link or button) with GSAP motion.
 * Pass only layout/slot classes via `className` — padding and text size come from `size`.
 *
 * @param {object} props
 * @param {string} [props.href] — if set, renders `<a>`; otherwise `<button>`
 * @param {string} props.label — visible + aria label (chars animate on hover)
 * @param {import('react').ReactNode} [props.icon]
 * @param {boolean} [props.reducedMotion]
 * @param {'hero' | 'light'} [props.variant]
 * @param {'dark' | 'light'} [props.surface] — focus ring offset (hero vs light sections)
 * @param {'md' | 'sm' | 'submit'} [props.size]
 * @param {string} [props.className] — position, width, margin, flex alignment only
 * @param {'button' | 'submit'} [props.type]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.heroCta] — sets `data-hero-cta` for homepage entrance animation
 * @param {boolean} [props.fullWidth] — with `size="md"`, keep full width at all breakpoints (e.g. Founder's desk)
 */
export default function MagneticButton({
  href,
  label,
  icon = null,
  reducedMotion = false,
  variant = 'hero',
  surface = 'dark',
  size = 'md',
  className = '',
  type = 'button',
  disabled = false,
  heroCta = false,
  fullWidth = false,
}) {
  const rootRef = useRef(null);
  const magneticRef = useRef(null);
  const idleGlowRef = useRef(null);
  const sheenRef = useRef(null);
  const rippleRef = useRef(null);
  const textHostRef = useRef(null);
  const iconRef = useRef(null);
  const particlesHostRef = useRef(null);
  const charElsRef = useRef([]);

  const xToRef = useRef(null);
  const yToRef = useRef(null);
  const hoverRef = useRef(false);

  const isAnchor = typeof href === 'string' && href.length > 0;

  useLayoutEffect(() => {
    if (reducedMotion || disabled) {
      xToRef.current = null;
      yToRef.current = null;
      return undefined;
    }
    const el = magneticRef.current;
    if (!el) return undefined;
    xToRef.current = gsap.quickTo(el, 'x', { duration: 0.65, ease: 'power3.out' });
    yToRef.current = gsap.quickTo(el, 'y', { duration: 0.65, ease: 'power3.out' });
    return undefined;
  }, [reducedMotion, disabled]);

  useLayoutEffect(() => {
    const host = textHostRef.current;
    if (!host) return undefined;
    charElsRef.current = fillCharSpans(host, label);
    return undefined;
  }, [label]);

  useLayoutEffect(() => {
    if (reducedMotion || disabled) return undefined;
    const ctx = gsap.context(() => {
      runIdleAnimations(
        { idleGlow: idleGlowRef.current, sheen: sheenRef.current },
        reducedMotion
      );
    });
    return () => {
      ctx.revert();
    };
  }, [reducedMotion, disabled, label]);

  const resetMagnetic = useCallback(() => {
    if (reducedMotion || disabled) return;
    xToRef.current?.(0);
    yToRef.current?.(0);
    if (magneticRef.current) {
      gsap.to(magneticRef.current, {
        scale: 1,
        duration: 0.55,
        ease: 'power3.out',
      });
    }
  }, [reducedMotion, disabled]);

  const onMove = useCallback(
    (e) => {
      if (reducedMotion || disabled || !rootRef.current) return;
      const r = rootRef.current.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) / (r.width / 2);
      const dy = (e.clientY - cy) / (r.height / 2);
      const mx = Math.max(-1, Math.min(1, dx)) * 10;
      const my = Math.max(-1, Math.min(1, dy)) * 8;
      xToRef.current?.(mx);
      yToRef.current?.(my);
    },
    [reducedMotion, disabled]
  );

  const onEnter = useCallback(() => {
    if (disabled) return;
    hoverRef.current = true;
    if (reducedMotion || !magneticRef.current) return;
    playHoverRipple(rippleRef.current, reducedMotion);
    playTextHoverStagger(charElsRef.current, reducedMotion);
    gsap.to(magneticRef.current, {
      scale: 1.05,
      duration: 0.75,
      ease: 'elastic.out(1, 0.55)',
    });
    if (iconRef.current) {
      gsap.to(iconRef.current, {
        x: 3,
        y: -3,
        duration: 0.45,
        ease: 'power2.out',
      });
    }
  }, [reducedMotion, disabled]);

  const onLeave = useCallback(() => {
    hoverRef.current = false;
    resetMagnetic();
    if (reducedMotion || disabled) return;
    if (iconRef.current) {
      gsap.to(iconRef.current, { x: 0, y: 0, duration: 0.4, ease: 'power2.out' });
    }
  }, [reducedMotion, disabled, resetMagnetic]);

  const onDown = useCallback(() => {
    if (reducedMotion || disabled || !magneticRef.current) return;
    gsap.to(magneticRef.current, {
      scale: hoverRef.current ? 0.97 : 0.96,
      duration: 0.07,
      ease: 'power2.in',
    });
  }, [reducedMotion, disabled]);

  const onUp = useCallback(() => {
    if (reducedMotion || disabled || !magneticRef.current) return;
    const target = hoverRef.current ? 1.05 : 1;
    gsap.to(magneticRef.current, {
      scale: target,
      duration: 0.42,
      ease: 'elastic.out(1, 0.55)',
    });
  }, [reducedMotion, disabled]);

  const onKeyDown = useCallback((e) => {
    if (e.key !== ' ') return;
    e.preventDefault();
    rootRef.current?.click();
  }, []);

  const onClick = useCallback(() => {
    if (disabled) return;
    playParticleBurst(particlesHostRef.current, reducedMotion);
  }, [reducedMotion, disabled]);

  useLayoutEffect(() => {
    return () => {
      const el = magneticRef.current;
      killTweensOf(el);
    };
  }, []);

  const isLight = variant === 'light';
  const faceBg = isLight ? 'bg-white/88 text-brand-forest' : 'bg-brand-lime text-brand-forest';
  const faceSurface = isLight
    ? 'backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_10px_28px_-8px_rgba(0,0,0,0.2)]'
    : 'backdrop-blur-none shadow-[inset_0_1px_0_rgba(255,255,255,0.55),0_10px_28px_-8px_rgba(0,0,0,0.25)]';

  const ringOffset =
    surface === 'light'
      ? 'focus-visible:ring-offset-2 focus-visible:ring-offset-white'
      : 'focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a1812]/80';

  const rootClass = [
    'group relative isolate flex justify-center font-semibold no-underline',
    'touch-manipulation select-none rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-lime/90',
    ringOffset,
    disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
    className,
  ].join(' ');

  const innerClasses = [
    'relative flex overflow-hidden rounded-[15px] border-0 font-semibold',
    INNER_SIZE[size],
    faceBg,
    faceSurface,
  ].join(' ');

  const dataHero = heroCta ? { 'data-hero-cta': true } : {};

  const magneticWidthClass =
    fullWidth && size === 'md'
      ? MAGNETIC_WIDTH_MD_FULL
      : MAGNETIC_WIDTH[size] || MAGNETIC_WIDTH.md;

  const inner = (
    <>
      <span
        ref={particlesHostRef}
        className="pointer-events-none absolute -inset-10 z-30 overflow-visible"
        aria-hidden
      />

      <span ref={magneticRef} className={magneticWidthClass}>
        <span
          ref={idleGlowRef}
          className="pointer-events-none absolute -inset-4 -z-10 rounded-[1.2rem] bg-gradient-to-br from-brand-lime/40 via-emerald-400/22 to-transparent opacity-38 blur-2xl"
          aria-hidden
        />

        <span className="relative block rounded-2xl">
          <span className={innerClasses}>
            <span
              ref={sheenRef}
              className={`pointer-events-none absolute -left-[40%] top-0 z-0 h-full w-1/2 -skew-x-12 bg-gradient-to-r from-transparent to-transparent opacity-30 ${
                isLight ? 'via-white/25' : 'via-white/40'
              }`}
              aria-hidden
            />

            <span
              ref={rippleRef}
              className={`pointer-events-none absolute inset-0 z-[1] rounded-[15px] ${
                isLight
                  ? 'bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.5),transparent_68%)]'
                  : 'bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.65),transparent_65%)]'
              }`}
              aria-hidden
            />

            <span
              ref={textHostRef}
              className="relative z-[2] flex flex-wrap justify-center gap-0 leading-tight"
              aria-hidden="true"
            />

            {icon ? (
              <span ref={iconRef} className="relative z-[2] inline-flex will-change-transform [&_svg]:shrink-0">
                {icon}
              </span>
            ) : null}
          </span>
        </span>
      </span>
    </>
  );

  if (isAnchor) {
    return (
      <a
        ref={rootRef}
        href={href}
        className={rootClass}
        aria-label={label}
        {...dataHero}
        onMouseMove={onMove}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onMouseDown={onDown}
        onMouseUp={onUp}
        onBlur={resetMagnetic}
        onKeyDown={onKeyDown}
        onClick={onClick}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      ref={rootRef}
      type={type}
      disabled={disabled}
      className={rootClass}
      aria-label={label}
      {...dataHero}
      onMouseMove={onMove}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onMouseDown={onDown}
      onMouseUp={onUp}
      onBlur={resetMagnetic}
      onKeyDown={onKeyDown}
      onClick={onClick}
    >
      {inner}
    </button>
  );
}
